<template>
  <section class="audio_video_page_wrapper">
    <div class="setup-content">

      <section class="section-card full-width">
        <div class="title-header-check">
          <h3 class="check-title">Device Configuration</h3>
        </div>

        <div class="config-grid">
          <div class="config-item">
            <div class="config-info">
              <label class="config-label">Microphone</label>
              <p class="config-desc">Test your audio levels before starting.</p>
            </div>
            <button
              @click="getMic"
              :class="['action-button', micButtonState]"
            >
              Check Microphone
            </button>
            <div v-if="mic_error" class="error-msg">{{ mic_error }}</div>
          </div>

          <div class="config-item">
            <div class="config-info">
              <label class="config-label">Display Source</label>
              <p class="config-desc">Required: Select the game window.</p>
            </div>
            <button
              @click="getScreen"
              :class="['action-button', screenButtonState]"
              :disabled="!micDone"
            >
              Choose Screen
            </button>
          </div>
        </div>
      </section>

      <div class="previews-row">
        <div class="section-card flex-grow-2">
          <div class="section-header">
            <h3 class="section-subtitle">Screen Share Preview</h3>
          </div>
          <div class="video-container">
            <video
              ref="screenVideo"
              autoplay
              muted
              class="video-frame"
            ></video>
            <div v-if="!screenStream" class="placeholder-overlay">
              <span>No Screen Selected</span>
            </div>
          </div>
        </div>

        <div class="section-card flex-grow-1">
          <div class="section-header">
            <h3 class="section-subtitle">Webcam Preview</h3>
          </div>

          <div class="webcam-interface">
            <div class="status-controls">
              <button
                :class="['action-button', webcamButtonState]"
                :disabled="!screenDone"
                @click="toggleWebcam"
              >
                {{ isWebcamVisible ? 'Disable Webcam' : 'Enable Webcam' }}
              </button>
              <p class="helper-text">Include webcam? (optional)</p>
            </div>

            <div class="video-container">
              <video
                v-show="isWebcamVisible"
                ref="videoElement"
                class="video-frame"
                autoplay
              ></video>
              <div v-if="!isWebcamVisible" class="placeholder-overlay">
                <span>Camera Off</span>
              </div>
            </div>
            <div v-if="error" class="error-msg">{{ error }}</div>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
export default {
  name: "PlaytestAudioVisualCheck",
  data: function() {
    return {
      isWebcamVisible: false,
      videostream: null,
      audiostream: null,
      screenStream: null,
      error: null,
      mic_error: null,
      toggleInProgress: false,
      contacts: [],
      micDone: false,
      screenDone: false,
    };
  },
  computed: {
    micButtonState: function() {
      if (this.micDone) return 'btn-completed';
      return 'btn-todo';
    },
    screenButtonState: function() {
      if (this.screenDone) return 'btn-completed';
      if (this.micDone) return 'btn-todo';
      return 'btn-disabled';
    },
    webcamButtonState: function() {
      if (this.isWebcamVisible) return 'btn-completed';
      if (this.screenDone) return 'btn-todo';
      return 'btn-disabled';
    },
  },
  beforeDestroy: function() {
    var streams = [this.videostream, this.audiostream, this.screenStream];
    streams.forEach(function(stream) {
      if (stream) {
        stream.getTracks().forEach(function(track) { track.stop(); });
      }
    });
  },
  methods: {
    sortContacts: function(contactsArray) {
      contactsArray.sort(function(a, b) {
        var getTime = function(ts) {
          if (ts && ts.toDate) return ts.toDate().getTime();
          if (ts instanceof Date) return ts.getTime();
          return 0;
        };
        return getTime(b.lastMessageAt) - getTime(a.lastMessageAt);
      });
      this.contacts = contactsArray;
    },
    async toggleWebcam() {
      if (!this.screenDone) return;
      if (this.toggleInProgress) return;
      this.toggleInProgress = true;
      try {
        if (!this.isWebcamVisible) {
          this.error = null;
          this.videostream = await navigator.mediaDevices.getUserMedia({ video: true });
          this.$refs.videoElement.srcObject = this.videostream;
          this.isWebcamVisible = true;
        } else {
          if (this.videostream) {
            this.videostream.getTracks().forEach(function(t) { t.stop(); });
          }
          this.isWebcamVisible = false;
        }
      } catch (err) {
        this.error = "Camera access denied or device not found.";
      } finally {
        var self = this;
        setTimeout(function() { self.toggleInProgress = false; }, 500);
      }
    },
    async getMic() {
      try {
        this.audiostream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.mic_error = null;
        this.micDone = true;
        alert("Microphone detected!");
      } catch (err) {
        this.mic_error = "Could not access microphone.";
        this.micDone = false;
      }
    },
    async getScreen() {
      if (!this.micDone) return;
      try {
        this.screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        this.$refs.screenVideo.srcObject = this.screenStream;
        this.screenDone = true;
        this.$emit('screen_done');
      } catch (err) {
        console.error("Screen share cancelled", err);
        this.screenDone = false;
      }
    },
  },
};
</script>

<style scoped>
.audio_video_page_wrapper {
  background-color: #eeeeee;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
}

.setup-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
}

.title-header-check {
  background-color: white;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  border-bottom: 2px solid #ff7222;
  margin-bottom: 12px;
}

.check-title {
  color: #ff7222;
  font-weight: bold;
  font-size: 1.5rem;
  margin: 0;
}

/* ── Preview row ── */
.previews-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

/* Flex basis uses percentages so cards never force overflow */
.flex-grow-2 { flex: 2 1 min(600px, 100%); }
.flex-grow-1 { flex: 1 1 min(320px, 100%); }

.section-card {
  background: white;
  border-radius: 14px;
  padding: 25px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  min-width: 0;
}

.section-header {
  border-bottom: 2px solid #ff7222;
  margin-bottom: 20px;
  padding-bottom: 8px;
}

.section-subtitle {
  color: #ff7222;
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0;
}

/* ── Video containers ── */
.video-container {
  background: #1a1a1a;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 16 / 9;
}

.video-frame {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.placeholder-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #888;
  font-weight: bold;
  background: rgba(0,0,0,0.05);
}

/* ── Webcam controls ── */
.status-controls {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.helper-text {
  font-size: 0.95rem;
  color: #666;
  margin: 0;
}

/* ── Buttons ── */
.action-button {
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-weight: bold;
  cursor: pointer;
  color: white;
  transition: transform 0.1s, opacity 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.action-button:active { transform: scale(0.98); }

/* Blue  — to do / newly unlocked */
.btn-todo {
  background-color: #17a2b8;
  cursor: pointer;
}

/* Orange — completed, can redo */
.btn-completed {
  background-color: #ff7222;
  cursor: pointer;
}

/* Grey  — not yet available */
.btn-disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.65;
}

/* ── Config grid ── */
.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 20px;
}

.config-item {
  display: flex;
  align-items: center;
  gap: 15px;
  background: #fdfdfd;
  padding: 15px;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  flex-wrap: wrap;
}

.config-info {
  flex: 1;
  min-width: 0;
}

.config-label {
  font-weight: bold;
  color: #333;
  font-size: 1.1rem;
}

.config-desc {
  font-size: 0.85rem;
  color: #777;
  margin: 0;
}

.error-msg {
  color: #db3360;
  font-size: 12px;
  font-weight: bold;
  margin-top: 5px;
  width: 100%;
}

/* ── Responsive breakpoints ── */

/* Tablet */
@media (max-width: 992px) {
  .flex-grow-2,
  .flex-grow-1 {
    flex: 1 1 100%;
  }
}

/* Mobile */
@media (max-width: 600px) {
  .audio_video_page_wrapper {
    padding: 12px;
  }

  .setup-content {
    gap: 14px;
  }

  .section-card {
    padding: 16px;
  }

  .check-title {
    font-size: 1.2rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  .config-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-button {
    width: 100%;
    text-align: center;
  }

  .status-controls {
    gap: 10px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/playtest/Playtest-AudioVisualCheck.vue